import React, { useEffect, useRef, useState } from "react";
import { Avatar, Badge, Button, Divider, List, Popover, Tabs, Tooltip, Typography } from "antd";
import { BellOutlined, BellFilled } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import Axios from "axios";

import {
  bp_get_batch_notification_for_employee,
  get_notification_for_recipe_approval_action,
  handleSessionExpired,
} from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';
import { useResponsive } from '@/hooks/useResponsive';
import { useTheme } from '@/App';
import { formatDate, formatDateTime } from '@/utils/datetime';

const deviation_color = (per) => {
  let color;
  let bg_color;

  switch (true) {
    case per >= 0 && per <= 5:
      color = defaultColors.success;
      bg_color = "#CCFFCC";
      break;
    case (per >= -10 && per < 0) || (per > 5 && per <= 50):
      color = defaultColors.warning;
      bg_color = "#FFF59E";
      break;
    case per < -10 || per > 50:
      color = defaultColors.error;
      bg_color = "#FFCCCC";
      break;
    default:
      color = defaultColors.text.primary;
  }
  return { color: color, bg_color: bg_color };
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const Notification = () => {
  const { isDark } = useTheme();
  const { isMobile } = useResponsive();
  const notification_refresh = useSelector((state) => state.notification_refresh);
  // recipe_type sent as string 'true' (Production) / 'false' (Test).
  const recipeTypeFlag = useSelector((state) => state.testProdView) === 'production';
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [notifications_data, set_notifications_data] = useState([]);
  const [alert_data, set_alert_data] = useState([]);
  const [activeTab, setActiveTab] = useState('1');
  // Real ref so the polling interval can be reliably cleared on 401/unmount.
  const pollRef = useRef(null);

  const handleToggle = (newOpen) => {
    setOpen(newOpen);
    if (newOpen) {
      bp_get_batch_notification_for_employee(success_get_notification, { recipe_type: recipeTypeFlag });
    }
  };

  const success_get_notification = (res) => {
    set_notifications_data(res.data.data);
  };

  const success_get_alerts = (res) => {
    set_alert_data(res.data.data);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === '2' && !alert_data?.length) {
      get_notification_for_recipe_approval_action(success_get_alerts, { recipe_type: recipeTypeFlag });
    }
  };

  useEffect(() => {
    // Stop polling + hand a 401 to the single global session-expired handler
    // (redirects to login ONCE, clears storage). Doing it here too prevents the
    // 30s interval from firing again after the session is gone -> no loop.
    const stopAndExpire = (msg) => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      handleSessionExpired(msg);
    };

    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('Token');
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
        const [alertRes, notifRes] = await Promise.allSettled([
          Axios.get('/bp/get_notification_for_recipe_approval_action', authHeaders),
          Axios.get('/bp/bp_get_batch_notification_for_employee', authHeaders),
        ]);

        if (alertRes.status === 'fulfilled') {
          set_alert_data(alertRes.value?.data?.data || []);
        } else if (alertRes.reason?.response?.status === 401) {
          stopAndExpire(alertRes.reason?.response?.data?.message);
          return;
        }

        if (notifRes.status === 'fulfilled') {
          set_notifications_data(notifRes.value?.data?.data || []);
        } else if (notifRes.reason?.response?.status === 401) {
          stopAndExpire(notifRes.reason?.response?.data?.message);
          return;
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          stopAndExpire(err?.response?.data?.message);
        }
      }
    };

    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, 30000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const renderNotificationItem = (item, index) => (
    <React.Fragment key={index}>
      <List.Item className="py-2">
        <div className="flex w-full gap-3">
          <Avatar
            size={36}
            style={{
              color: defaultColors.success,
              backgroundColor: `${defaultColors.success}20`,
              flexShrink: 0
            }}
          >
            {item.recipe_name
              .split(" ")
              .map((word) => word[0])
              .join("")
              .toUpperCase()}
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2 mb-2">
              <Typography.Paragraph
                ellipsis={{
                  rows: 2,
                  expandable: true,
                  symbol: "more",
                }}
                className="mb-0"
              >
                {item?.msg}
              </Typography.Paragraph>
              <div className="flex flex-col text-xs text-right" style={{ color: defaultColors.text.secondary }}>
                <div>{formatDate(item?.batch_schedule_time)}</div>
                <div>{formatDateTime(item?.batch_schedule_time, 'HH:mm:ss')}</div>
              </div>
            </div>
            <div className="flex gap-4 text-xs" style={{ color: defaultColors.text.secondary }}>
              <div>Batch: {item?.batch_number}</div>
              <div>Process: {item?.process_name}</div>
            </div>
          </div>
        </div>
      </List.Item>
      <Divider className="my-0" />
    </React.Fragment>
  );

  const renderAlertItem = (item, index) => (
    <React.Fragment key={index}>
      <List.Item className="py-2">
        <div className="flex w-full gap-3">
          <Avatar
            size={36}
            style={{
              color: defaultColors.warning,
              backgroundColor: 'white',
              border: `1px solid ${defaultColors.warning}`,
              flexShrink: 0
            }}
          >
            {item?.recipe_name
              .split(" ")
              .map((word) => word[0])
              .join("")
              .toUpperCase()}
          </Avatar>
          <div className="flex-1 min-w-0">
            <Typography.Paragraph
              ellipsis={{
                rows: 2,
                expandable: true,
                symbol: "more",
              }}
              className="mb-2"
            >
              {item?.msg}
            </Typography.Paragraph>
            <div className="text-xs" style={{ color: defaultColors.text.secondary }}>
              Date: {formatDateTime(item?.date)}
            </div>
          </div>
        </div>
      </List.Item>
      <Divider className="my-0" />
    </React.Fragment>
  );

  const tabItems = [
    {
      key: '1',
      label: (
        <div className="flex items-center gap-2">
          <BellOutlined style={{ color: defaultColors.success }} />
          <span>Notifications</span>
        </div>
      ),
      children: (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <List
            dataSource={notifications_data?.slice(0, 5)}
            renderItem={renderNotificationItem}
          />
          <Divider />
          <div className="text-center py-3">
            <Button
              type="link"
              onClick={() => {
                navigate("/notifications");
                setOpen(false);
              }}
            >
              <h6 className="text-lg font-semibold m-0" style={{ color: defaultColors.primary }}>
                View All
              </h6>
            </Button>
          </div>
        </div>
      )
    },
    {
      key: '2',
      label: (
        <div className="flex items-center gap-2">
          <Badge count={alert_data?.length} size="small">
            <BellFilled style={{ color: defaultColors.warning }} />
          </Badge>
          <span>Approval Notifications</span>
        </div>
      ),
      children: (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <List
            dataSource={alert_data?.slice(0, 5)}
            renderItem={renderAlertItem}
          />
          <Divider />
          <div className="text-center py-3">
            <Button
              type="link"
              onClick={() => {
                navigate("/notifications");
                setOpen(false);
              }}
            >
              <h6 className="text-lg font-semibold m-0" style={{ color: defaultColors.primary }}>
                View All
              </h6>
            </Button>
          </div>
        </div>
      )
    }
  ];

  const popoverContent = (
    <div style={{ width: isMobile ? 285 : 420 }}>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        centered
      />
    </div>
  );

  // Dark mode aware colors
  const bgColor = isDark
    ? (open ? defaultColors.grey[600] : defaultColors.grey[700])
    : (open ? defaultColors.grey[300] : defaultColors.grey[100]);
  const iconColor = isDark ? defaultColors.grey[100] : defaultColors.grey[700];

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleToggle}
      placement={isMobile ? "bottom" : "bottomRight"}
    >
      <Tooltip title="Notifications" zIndex={9999}>
        <Button
          type="text"
          shape="circle"
          icon={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Badge count={alert_data?.length + notifications_data?.length} size="small">
                <BellOutlined style={{ fontSize: 23, display: 'block', color: iconColor }} />
              </Badge>
            </div>
          }
          style={{
            backgroundColor: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        />
      </Tooltip>
    </Popover>
  );
};

export default Notification;
